import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.top = OFFSET + 'px';

    if (this.matches('.sorting-filter__options') && document.querySelector('.mh-collection-editorial')) {
      const anchor = this.closest('details');
      if (anchor) {
        const anchorRect = anchor.getBoundingClientRect();
        const previewBar = document.getElementById('PBarNextFrame');
        const previewHeight = previewBar ? previewBar.getBoundingClientRect().height : 0;
        const header = document.querySelector('header-component');
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const below = Math.max(0, window.innerHeight - previewHeight - anchorRect.bottom - 20);
        const above = Math.max(0, anchorRect.top - headerBottom - 20);
        const openAbove = below < 220 && above > below;
        const available = openAbove ? above : below;
        const panelHeight = Math.max(100, Math.min(380, available));
        this.style.maxHeight = panelHeight + 'px';
        this.style.overflowY = 'auto';
        this.style.top = openAbove ? '-' + (panelHeight + 8) + 'px' : anchorRect.height + 8 + 'px';
      }
    }

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #onToggle = () => {
    if (this.closest('details')?.open) this.#updatePosition();
  };

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);
    this.closest('details')?.addEventListener('toggle', this.#onToggle);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.closest('details')?.removeEventListener('toggle', this.#onToggle);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
